import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, FolderOpen, DollarSign, Briefcase, TrendingUp, Clock, AlertCircle, Star, ArrowRight, Calendar, Shield, CheckSquare, UserPlus, ThumbsUp, Mail, MessageSquare } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import './AdminPages.css';

const revenueData = [
  { month: 'Sep', revenue: 45000 }, { month: 'Oct', revenue: 62000 },
  { month: 'Nov', revenue: 48000 }, { month: 'Dec', revenue: 78000 },
  { month: 'Jan', revenue: 85000 }, { month: 'Feb', revenue: 92000 },
];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{label}</p>
      <p style={{ fontWeight: 700, color: 'var(--secondary-color)' }}>R {payload[0].value?.toLocaleString()}</p>
    </div>
  );
};

function contactSortMs(c) {
  const t = c?.createdAt;
  if (t?.seconds != null) return t.seconds * 1000;
  if (typeof t === 'number') return t;
  return 0;
}

/** Recent website contact form submissions (roles with Firestore `contacts` read). */
function RecentEnquiriesCard({ contacts }) {
  const rows = useMemo(
    () => [...(contacts || [])].sort((a, b) => contactSortMs(b) - contactSortMs(a)).slice(0, 6),
    [contacts]
  );
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <p className="card-title" style={{ marginBottom: 4 }}>
            Website enquiries
          </p>
          <p className="card-subtitle">Messages from the public contact form</p>
        </div>
        <Link to="/admin/contacts" className="btn-ghost" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
          Open inbox <ArrowRight size={13} />
        </Link>
      </div>
      {rows.length === 0 && <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>No messages yet.</p>}
      {rows.map((c) => (
        <div
          key={c.id}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '12px 0',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(201,162,39,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Mail size={16} style={{ color: 'var(--secondary-color)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 4 }}>{c.name}</p>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>{c.email}</p>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <MessageSquare size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--text-dim)' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{c.message || '—'}</span>
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Founder / Super Admin Dashboard ─── */
function FounderDashboard({ data }) {
  const totalRevenue = data.invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + i.amount, 0);
  const pendingApprovals = data.approvalRequests.filter(a => a.status !== 'Approved').length;
  const activeProjects = data.projects.filter(p => p.status !== 'Completed').length;
  return (
    <>
      <div className="stats-row">
        {[
          { icon: Users,      value: data.employees.length,   label: 'Total Employees',   change: '+1 this month', up: true },
          { icon: FolderOpen, value: activeProjects,           label: 'Active Projects',   change: `${data.projects.length} total`, up: null },
          { icon: Briefcase,  value: data.clients.length,      label: 'Clients',           change: '+2 this quarter', up: true },
          { icon: DollarSign, value: `R${(totalRevenue/1000).toFixed(0)}k`, label: 'Revenue Collected', change: '↑ 8.2%', up: true },
          { icon: ThumbsUp,   value: pendingApprovals,         label: 'Pending Approvals', change: 'Require sign-off', up: null },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon"><s.icon size={18}/></div><span className={`stat-change ${s.up===true?'up':s.up===false?'down':'neutral'}`}>{s.change}</span></div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="two-col" style={{ marginBottom: 20 }}>
        <div className="card">
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
            <div><p className="card-title">Revenue Trend</p><p className="card-subtitle">Last 6 months</p></div>
            <span className="badge badge-success"><TrendingUp size={10}/> Growing</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={revenueData}>
              <defs><linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#C9A227" stopOpacity={0.3}/><stop offset="95%" stopColor="#C9A227" stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="month" tick={{ fill:'#888',fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis hide/><Tooltip content={<ChartTip/>}/>
              <Area type="monotone" dataKey="revenue" stroke="#C9A227" strokeWidth={2} fill="url(#gGold)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          {/* Pending Approvals */}
          <div className="card">
            <p className="card-title" style={{ marginBottom:12 }}>Pending Approvals</p>
            {data.approvalRequests.filter(a=>a.status!=='Approved').map(a => (
              <div key={a.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0',borderBottom:'1px solid var(--border-color)' }}>
                <div>
                  <p style={{ fontSize:'0.85rem',fontWeight:600 }}>{a.type} — {a.ref}</p>
                  <p style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>by {a.requestedByName}</p>
                </div>
                <Link to="/admin/approvals" className="btn-gold" style={{ padding:'5px 12px',fontSize:'0.72rem' }}>Review</Link>
              </div>
            ))}
            {data.approvalRequests.filter(a=>a.status!=='Approved').length===0 && <p style={{ fontSize:'0.8rem',color:'var(--text-dim)' }}>All approvals up to date ✓</p>}
          </div>

          {/* Alerts */}
          <div className="card">
            <p className="card-title" style={{ marginBottom:10 }}>Alerts</p>
            {data.invoices.filter(i=>i.status==='Overdue').length>0 && <div className="info-banner info-banner-red" style={{ margin:'4px 0' }}><AlertCircle size={15}/><span>{data.invoices.filter(i=>i.status==='Overdue').length} invoice(s) overdue</span></div>}
            <div className="info-banner info-banner-blue" style={{ margin:'4px 0' }}><UserPlus size={15}/><span>{data.applicants.filter(a=>a.status==='Applied').length} new applicants waiting review</span></div>
          </div>
        </div>
      </div>

      {/* Active projects */}
      <div className="card">
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
          <p className="card-title">Active Projects</p>
          <Link to="/admin/projects" className="btn-ghost" style={{ fontSize:'0.8rem',display:'flex',alignItems:'center',gap:4 }}>See all <ArrowRight size={13}/></Link>
        </div>
        {data.projects.filter(p=>p.status!=='Completed').map(p => (
          <div key={p.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid var(--border-color)' }}>
            <div style={{ flex:1 }}><p style={{ fontSize:'0.875rem',fontWeight:700 }}>{p.name}</p><p style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>{p.client}</p></div>
            <div style={{ width:100 }}>
              <div className="progress-track"><div className="progress-fill" style={{ width:`${p.progress}%` }}/></div>
            </div>
            <span style={{ fontSize:'0.75rem',fontWeight:700,color:'var(--secondary-color)',fontFamily:'var(--font-mono)',width:36,textAlign:'right' }}>{p.progress}%</span>
            <span className={`badge ${p.status==='In Progress'?'badge-info':p.status==='Review'?'badge-warning':'badge-gray'}`}>{p.status}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── HR Dashboard ─── */
function HRDashboard({ data }) {
  const open = data.jobs.filter(j=>j.status==='Open').length;
  const newApps = data.applicants.filter(a=>a.status==='Applied').length;
  const hired = data.applicants.filter(a=>a.status==='Hired').length;
  const pendingLeave = data.leaveRequests.filter(l=>l.status==='Pending').length;
  return (
    <>
      <div className="stats-row">
        {[
          { icon: UserPlus,   value: open,                  label: 'Open Positions',   color: 'var(--info)' },
          { icon: Users,      value: newApps,                label: 'New Applicants',   color: 'var(--secondary-color)' },
          { icon: CheckSquare,value: hired,                  label: 'Hired This Month', color: 'var(--success)' },
          { icon: Calendar,   value: pendingLeave,           label: 'Leave Requests',   color: 'var(--warning)' },
          { icon: Users,      value: data.employees.length,  label: 'Total Staff',      color: 'var(--text-muted)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon" style={{ background:`${s.color}15`,color:s.color }}><s.icon size={18}/></div></div>
            <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <p className="card-title" style={{ marginBottom:14 }}>Recent Applicants</p>
          {data.applicants.slice(0,5).map(a => (
            <div key={a.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div className="av av-sm av-gold">{a.name.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
              <div style={{ flex:1 }}><p style={{ fontSize:'0.85rem',fontWeight:600 }}>{a.name}</p><p style={{ fontSize:'0.73rem',color:'var(--text-muted)' }}>{a.job}</p></div>
              <span className={`badge ${a.status==='Applied'?'badge-info':a.status==='Shortlisted'?'badge-gold':a.status==='Interview Scheduled'?'badge-warning':a.status==='Hired'?'badge-success':'badge-danger'}`}>{a.status}</span>
            </div>
          ))}
          <Link to="/admin/recruitment" className="btn-gold" style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:14,width:'100%',padding:9,fontSize:'0.82rem' }}>
            Manage Recruitment <ArrowRight size={14}/>
          </Link>
        </div>
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          <div className="card">
            <p className="card-title" style={{ marginBottom:12 }}>Leave Requests</p>
            {data.leaveRequests.map(l => (
              <div key={l.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-color)' }}>
                <div><p style={{ fontSize:'0.82rem',fontWeight:600 }}>{l.employee}</p><p style={{ fontSize:'0.72rem',color:'var(--text-muted)' }}>{l.type} · {l.from}</p></div>
                <span className={`badge ${l.status==='Approved'?'badge-success':l.status==='Pending'?'badge-warning':'badge-danger'}`}>{l.status}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <p className="card-title" style={{ marginBottom:12 }}>Today's Attendance</p>
            {data.attendance.map(a => (
              <div key={a.id} style={{ display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid var(--border-color)' }}>
                <div className={`att-status-dot`} style={{ background:a.status==='Present'?'var(--success)':a.status==='Absent'?'var(--danger)':'var(--warning)' }}/>
                <span style={{ fontSize:'0.82rem',flex:1 }}>{a.employee}</span>
                <span className={`badge ${a.status==='Present'?'badge-success':a.status==='Absent'?'badge-danger':'badge-warning'}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Mentor Dashboard ─── */
function MentorDashboard({ data, user }) {
  const myProjects = data.projects.filter(p => String(p.mentorId) === String(user?.employeeId));
  const myTasks = data.tasks.filter(t => myProjects.some(p=>p.id===t.projectId));
  const inProgress = myTasks.filter(t=>t.status==='In Progress').length;
  return (
    <>
      <div className="stats-row">
        {[
          { icon: FolderOpen, value: myProjects.length, label: 'My Projects' },
          { icon: Users,      value: myProjects.reduce((s,p)=>s+p.team.length,0), label: 'Team Members' },
          { icon: CheckSquare,value: inProgress,        label: 'Tasks In Progress' },
          { icon: Clock,      value: myProjects.filter(p=>p.status==='Review').length, label: 'Under Review' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon"><s.icon size={18}/></div></div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <p className="card-title" style={{ marginBottom:14 }}>My Projects</p>
          {myProjects.map(p => (
            <div key={p.id} style={{ padding:'12px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                <p style={{ fontSize:'0.9rem',fontWeight:700 }}>{p.name}</p>
                <span className={`badge ${p.status==='In Progress'?'badge-info':p.status==='Review'?'badge-warning':p.status==='Completed'?'badge-success':'badge-gray'}`}>{p.status}</span>
              </div>
              <div className="progress-track"><div className="progress-fill" style={{ width:`${p.progress}%` }}/></div>
              <div style={{ display:'flex',justifyContent:'space-between',marginTop:6,fontSize:'0.72rem',color:'var(--text-muted)' }}>
                <span>{p.team.length} team members</span><span>{p.progress}%</span>
              </div>
            </div>
          ))}
          <Link to="/admin/projects" className="btn-gold" style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:14,padding:9,fontSize:'0.82rem' }}>Manage Projects <ArrowRight size={14}/></Link>
        </div>
        <div className="card">
          <p className="card-title" style={{ marginBottom:12 }}>Active Tasks</p>
          {myTasks.filter(t=>t.status!=='Done').slice(0,6).map(t => (
            <div key={t.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ flex:1 }}><p style={{ fontSize:'0.82rem',fontWeight:600 }}>{t.title}</p><p style={{ fontSize:'0.72rem',color:'var(--text-muted)' }}>Due: {t.due}</p></div>
              <span className={`badge ${t.priority==='High'?'badge-danger':t.priority==='Medium'?'badge-warning':'badge-gray'}`}>{t.priority}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Finance Dashboard ─── */
function FinanceDashboard({ data }) {
  const paid = data.invoices.filter(i=>i.status==='Paid').reduce((s,i)=>s+i.amount,0);
  const pending = data.invoices.filter(i=>i.status==='Pending').reduce((s,i)=>s+i.amount,0);
  const overdue = data.invoices.filter(i=>i.status==='Overdue').reduce((s,i)=>s+i.amount,0);
  return (
    <>
      <div className="stats-row">
        {[
          { icon: DollarSign, value:`R${(paid/1000).toFixed(0)}k`,    label:'Collected',   color:'var(--success)' },
          { icon: Clock,      value:`R${(pending/1000).toFixed(0)}k`, label:'Outstanding', color:'var(--warning)' },
          { icon: AlertCircle,value:`R${(overdue/1000).toFixed(0)}k`, label:'Overdue',     color:'var(--danger)' },
          { icon: TrendingUp, value:data.invoices.length,             label:'Total Invoices',color:'var(--secondary-color)' },
          { icon: FolderOpen, value:data.quotations.length,           label:'Quotations',  color:'var(--info)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon" style={{ background:`${s.color}15`,color:s.color }}><s.icon size={18}/></div></div>
            <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <p className="card-title" style={{ marginBottom:4 }}>Revenue Trend</p>
          <p className="card-subtitle" style={{ marginBottom:14 }}>Last 6 months</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={revenueData}>
              <defs><linearGradient id="gGold2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#C9A227" stopOpacity={0.3}/><stop offset="95%" stopColor="#C9A227" stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="month" tick={{ fill:'#888',fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis hide/><Tooltip content={<ChartTip/>}/>
              <Area type="monotone" dataKey="revenue" stroke="#C9A227" strokeWidth={2} fill="url(#gGold2)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <p className="card-title" style={{ marginBottom:12 }}>Recent Invoices</p>
          {data.invoices.slice(0,5).map(inv => (
            <div key={inv.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div><p style={{ fontSize:'0.82rem',fontWeight:700,fontFamily:'var(--font-mono)',color:'var(--secondary-color)' }}>{inv.id}</p><p style={{ fontSize:'0.72rem',color:'var(--text-muted)' }}>{inv.client}</p></div>
              <div style={{ textAlign:'right' }}><p style={{ fontSize:'0.85rem',fontWeight:700 }}>R {inv.amount.toLocaleString()}</p><span className={`badge ${inv.status==='Paid'?'badge-success':inv.status==='Pending'?'badge-warning':'badge-danger'}`}>{inv.status}</span></div>
            </div>
          ))}
          <Link to="/admin/finance" className="btn-gold" style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:14,padding:9,fontSize:'0.82rem' }}>View Finance <ArrowRight size={14}/></Link>
        </div>
      </div>
    </>
  );
}

/* ─── Admin Dashboard ─── */
function AdminDashboard({ data }) {
  return (
    <>
      <div className="stats-row">
        {[
          { icon: FolderOpen, value: data.projects.filter(p=>p.status!=='Completed').length, label:'Active Projects' },
          { icon: Users,      value: data.employees.length,  label:'Employees' },
          { icon: Briefcase,  value: data.clients.length,    label:'Clients' },
          { icon: Calendar,   value: data.meetings.length,   label:'Meetings' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon"><s.icon size={18}/></div></div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <p className="card-title" style={{ marginBottom:14 }}>Project Overview</p>
          {data.projects.map(p => (
            <div key={p.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'9px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ flex:1 }}><p style={{ fontSize:'0.85rem',fontWeight:700 }}>{p.name}</p><p style={{ fontSize:'0.73rem',color:'var(--text-muted)' }}>{p.client}</p></div>
              <div style={{ width:80 }}><div className="progress-track"><div className="progress-fill" style={{ width:`${p.progress}%` }}/></div></div>
              <span className={`badge ${p.status==='In Progress'?'badge-info':p.status==='Review'?'badge-warning':p.status==='Completed'?'badge-success':'badge-gray'}`}>{p.status}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <p className="card-title" style={{ marginBottom:12 }}>Upcoming Meetings</p>
          {data.meetings.map(m => (
            <div key={m.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ width:40,height:44,background:'rgba(201,162,39,0.1)',border:'1px solid var(--border-accent)',borderRadius:'var(--radius-md)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                <span style={{ fontFamily:'var(--font-display)',fontSize:'1rem',fontWeight:700,color:'var(--secondary-color)',lineHeight:1 }}>{new Date(m.date).getDate()}</span>
                <span style={{ fontSize:'0.58rem',color:'var(--text-muted)',textTransform:'uppercase' }}>{new Date(m.date).toLocaleString('default',{month:'short'})}</span>
              </div>
              <div><p style={{ fontSize:'0.82rem',fontWeight:600 }}>{m.title}</p><p style={{ fontSize:'0.72rem',color:'var(--text-muted)' }}>{m.time} · {m.platform}</p></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Employee Dashboard ─── */
function EmployeeDashboard({ data, user }) {
  const myProjects = data.projects.filter(p => (p.team || []).map(String).includes(String(user?.employeeId)));
  const myTasks = data.tasks.filter(t => String(t.assignedTo) === String(user?.employeeId));
  const doneTasks = myTasks.filter(t=>t.status==='Done').length;
  return (
    <>
      <div className="stats-row">
        {[
          { icon: FolderOpen, value: myProjects.length,     label:'My Projects' },
          { icon: CheckSquare,value: myTasks.length,        label:'My Tasks' },
          { icon: CheckSquare,value: doneTasks,             label:'Completed Tasks', color:'var(--success)' },
          { icon: Clock,      value: myTasks.filter(t=>t.status==='In Progress').length, label:'In Progress' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon" style={{ background:`${s.color||'var(--secondary-color)'}15`,color:s.color||'var(--secondary-color)' }}><s.icon size={18}/></div></div>
            <div className="stat-value" style={{ color:s.color||'var(--text-color)' }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <p className="card-title" style={{ marginBottom:14 }}>My Projects</p>
          {myProjects.length===0 && <p style={{ color:'var(--text-dim)',fontSize:'0.85rem' }}>You haven't been assigned to any projects yet.</p>}
          {myProjects.map(p => (
            <div key={p.id} style={{ padding:'12px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                <p style={{ fontSize:'0.9rem',fontWeight:700 }}>{p.name}</p>
                <span className={`badge ${p.status==='In Progress'?'badge-info':p.status==='Review'?'badge-warning':'badge-gray'}`}>{p.status}</span>
              </div>
              <div className="progress-track"><div className="progress-fill" style={{ width:`${p.progress}%` }}/></div>
              <p style={{ fontSize:'0.72rem',color:'var(--text-muted)',marginTop:5 }}>Deadline: {p.deadline}</p>
            </div>
          ))}
        </div>
        <div className="card">
          <p className="card-title" style={{ marginBottom:12 }}>My Tasks</p>
          {myTasks.length===0 && <p style={{ color:'var(--text-dim)',fontSize:'0.85rem' }}>No tasks assigned yet.</p>}
          {myTasks.map(t => (
            <div key={t.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border-color)' }}>
              <div style={{ flex:1 }}><p style={{ fontSize:'0.82rem',fontWeight:600 }}>{t.title}</p><p style={{ fontSize:'0.71rem',color:'var(--text-muted)' }}>Due: {t.due}</p></div>
              <span className={`badge ${t.status==='Done'?'badge-success':t.status==='In Progress'?'badge-info':'badge-gray'}`}>{t.status}</span>
            </div>
          ))}
          <Link to="/admin/tasks" className="btn-gold" style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:14,padding:9,fontSize:'0.82rem' }}>View All Tasks <ArrowRight size={14}/></Link>
        </div>
      </div>
    </>
  );
}

export default function DashboardHome() {
  const { user, role } = useAuth();
  const { data } = useApp();

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const roleLabels = { super_admin:'Founder', admin:'Administrator', hr:'HR Manager', mentor:'Mentor', finance:'Finance Manager', employee:'Employee' };
  const roleColors = { super_admin:'#C9A227', admin:'#3182ce', hr:'#38a169', mentor:'#805ad5', finance:'#d69e2e', employee:'#e53e3e' };

  return (
    <div className="admin-page">
      {/* Welcome banner */}
      <div className="card" style={{ marginBottom:24,background:'var(--surface-color)',borderColor:'var(--border-accent)',position:'relative',overflow:'hidden',padding:'24px 28px' }}>
        <div style={{ position:'absolute',right:-20,top:'50%',transform:'translateY(-50%)',fontFamily:'var(--font-display)',fontSize:'6rem',fontWeight:900,color:'rgba(201,162,39,0.04)',pointerEvents:'none',whiteSpace:'nowrap' }}>FORTITUDE</div>
        <div style={{ position:'relative',display:'flex',alignItems:'flex-end',justifyContent:'space-between' }}>
          <div>
            <span style={{ fontSize:'0.75rem',color:roleColors[role]||'var(--secondary-color)',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',display:'block',marginBottom:4 }}>{greeting()},</span>
            <h1 style={{ fontFamily:'var(--font-display)',fontSize:'2rem',fontWeight:700,marginBottom:6,display:'flex',alignItems:'center',gap:12 }}>
              {user?.name?.split(' ')[0]}
              <span style={{ fontSize:'0.72rem',fontFamily:'var(--font-body)',background:`${roleColors[role]||'#C9A227'}20`,border:`1px solid ${roleColors[role]||'#C9A227'}40`,color:roleColors[role]||'var(--secondary-color)',padding:'3px 10px',borderRadius:20,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase' }}>
                {roleLabels[role]}
              </span>
            </h1>
            <p style={{ fontSize:'0.85rem',color:'var(--text-muted)' }}>Here's what's happening at Fortitude IT today.</p>
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:8,fontSize:'0.8rem',color:'var(--text-dim)' }}>
            <Calendar size={14}/>
            <span>{new Date().toLocaleDateString('en-ZA',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span>
          </div>
        </div>
      </div>

      {role === 'super_admin' && <FounderDashboard  data={data}/>}
      {role === 'hr'          && <HRDashboard       data={data}/>}
      {role === 'mentor'      && <MentorDashboard   data={data} user={user}/>}
      {role === 'finance'     && <FinanceDashboard  data={data}/>}
      {role === 'admin'       && <AdminDashboard    data={data}/>}
      {role === 'employee'    && <EmployeeDashboard data={data} user={user}/>}

      {(role === 'super_admin' || role === 'admin' || role === 'hr') && <RecentEnquiriesCard contacts={data.contacts} />}
    </div>
  );
}
